import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, SxProps, Tooltip, Typography } from '@mui/material'
import Button from '@carsys/ui/src/Button'
import AddShoppingCart from '@mui/icons-material/AddShoppingCart'
import { useOrderButtonContext } from '@cbgms/components/src/order-parts/context/buttons/OrderButtonContext'
import { useFeature } from '@cbgms/base/feature-flags'
import { useNotifier } from '@cbgms/base/notifier'

interface IOrderButtonNewOrderlineProps {
  hasGenartMatchValue?: number
  size?: 'small' | 'medium' | 'large' | undefined
  disabled: boolean
  buttonText?: string
  styling?: SxProps
}

const OrderButtonNewOrderLine: React.FC<IOrderButtonNewOrderlineProps> = ({ hasGenartMatchValue, size, disabled, buttonText, styling }) => {
  const { t } = useTranslation()
  const notifier = useNotifier()
  const { translationNames, onClickNewOrderLine, unitsPerSale, recommendedQuantity, product, isOpEqualOpPromotionType } =
    useOrderButtonContext()
  const addQuantity = Math.max(recommendedQuantity || 0, unitsPerSale || 0, 1)
  const isOpOpOutOfStock = isOpEqualOpPromotionType && product?.TotalStock === 0
  const hasAxnlReqqtyHaynespro = useFeature('axnl-reqqty-haynespro')
  const addNewOrderLine = () => {
    if (addQuantity === 1 && isOpEqualOpPromotionType && addQuantity === (product?.TotalStock ?? 0)) {
      notifier.warning(t('order.max-quantity-reached.toaster.title1'), t('order.max-quantity-reached.toaster.message1'))
    }
    let newAddQuantity = Math.ceil(addQuantity)
    if (hasGenartMatchValue) {
      if (hasGenartMatchValue > newAddQuantity) {
        newAddQuantity = Math.ceil(hasGenartMatchValue / newAddQuantity) * addQuantity
      }
    }
    onClickNewOrderLine(newAddQuantity)
  }
  // without buttontext the button behaves like the small square (icon) button with shopping cart icon and tooltip
  // with buttontext the button behaves like the regular button with text and shopping cart icon without tooltip
  return (
    <Tooltip
      title={
        isOpOpOutOfStock ? (
          <Typography variant='caption'>{t('order.article-maximum-quantity-reached')}</Typography>
        ) : (
          !buttonText && (
            <Typography variant='caption'>
              {translationNames === 'order' ? t('order.article-add-basket') : t('order.article-add-to-workorder')}
              {addQuantity > 1 ? `: ${addQuantity}` : ''}
            </Typography>
          )
        )
      }
      arrow
      disableHoverListener={!isOpOpOutOfStock && !!buttonText}
      disableFocusListener={!!buttonText}
      disableTouchListener={!!buttonText}
    >
      <Box>
        <Button
          size={size || 'small'}
          color='primary'
          data-testid='orderbutton-new'
          variant='contained'
          startIcon={
            !buttonText ? (
              ''
            ) : (
              <>
                {addQuantity > 1 && !hasAxnlReqqtyHaynespro ? `${addQuantity} ` : ''}
                <AddShoppingCart />
              </>
            )
          }
          disabled={disabled || isOpOpOutOfStock}
          onClick={() => addNewOrderLine()}
          sx={{ alignSelf: !buttonText ? 'unset' : 'end', height: 40, minWidth: '11.4rem', marginRight: '5px', ...styling }}
        >
          {buttonText ? (
            <Typography variant='button' sx={{ whiteSpace: 'nowrap' }}>
              {buttonText}
            </Typography>
          ) : (
            <AddShoppingCart fontSize={size || 'small'} />
          )}
        </Button>
      </Box>
    </Tooltip>
  )
}

export default OrderButtonNewOrderLine
